import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Typography,
    Checkbox,
    FormControlLabel,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import styled from '@emotion/styled';
import { FormField, Row, HalfWidthField } from './sharedStyles';
import { useDispatch } from 'react-redux';
import { getPermissions } from '../../redux/ducks/permission';

const FirstFormField = styled(FormField)(({ theme }) => ({
    marginTop: theme.spacing(1),
}));

const PermissionsHeaderRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
}));

const HeaderActionsRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
}));

const PermissionsHelperText = styled(Typography)(({ theme }) => ({
    marginBottom: theme.spacing(1),
    display: 'block',
}));

const PermissionsSearchField = styled(TextField)(({ theme }) => ({
    marginBottom: theme.spacing(1),
}));

const PermissionsList = styled(Box)({
    maxHeight: 500,
    overflow: 'auto',
});

const EmptyStateText = styled(Typography)(({ theme }) => ({
    padding: theme.spacing(2),
}));

const PermissionAccordion = styled(Accordion, {
    shouldForwardProp: (prop) => prop !== 'hasSelection',
})(({ theme, hasSelection }) => ({
    '&:before': { display: 'none' },
    border: `1px solid ${hasSelection ? theme.palette.primary.main : theme.palette.divider}`,
    marginBottom: theme.spacing(1),
    borderRadius: '4px !important',
    overflow: 'hidden',
}));

const PermissionAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
    '& .MuiAccordionSummary-content': {
        alignItems: 'center',
        gap: theme.spacing(1),
    },
}));

const NoMarginCheckboxLabel = styled(FormControlLabel)({
    marginRight: 0,
});

const PermissionInfo = styled(Box)({
    flex: 1,
});

const CountChip = styled(Chip)(({ theme }) => ({
    marginRight: theme.spacing(1),
}));

const ChildrenDetails = styled(AccordionDetails)(({ theme }) => ({
    paddingTop: 0,
    paddingLeft: theme.spacing(6),
}));

const ChildCheckboxLabel = styled(FormControlLabel)(({ theme }) => ({
    display: 'flex',
    marginBottom: theme.spacing(0.25),
}));

const RoleDialog = ({ open, onClose, onSave, role, mode = 'create' }) => {
    const dispatch = useDispatch();
    const [formData, setFormData] = useState({
        name: '',
        type: 'General',
        level: 1,
        description: '',
        permissions: [],
    });
    const [availablePermissions, setAvailablePermissions] = useState([]);
    const [permissionSearch, setPermissionSearch] = useState('');

    useEffect(() => {
        if (open) {
            dispatch(getPermissions((data) => {
                setAvailablePermissions(data || []);
            }));
        }
    }, [open, dispatch]);

    useEffect(() => {
        if (role && mode === 'edit') {
            setFormData({
                name: role.name || '',
                type: role.type || 'General',
                level: role.level || 1,
                description: role.description || '',
                permissions: role.permissions || [],
            });
        } else {
            setFormData({
                name: '',
                type: 'General',
                level: 1,
                description: '',
                permissions: [],
            });
        }
    }, [role, mode, open]);

    // Stale keys from removed/renamed permissions can linger on a role and have no catalog checkbox
    // to clear them; drop anything not in the loaded catalog so it never round-trips to the API and
    // the "N selected" count matches the per-category sums. Runs once the catalog is available.
    useEffect(() => {
        if (!availablePermissions.length) return;
        const validSet = new Set();
        availablePermissions.forEach((p) => {
            validSet.add(p.name);
            (p.children || []).forEach((child) => validSet.add(child));
        });
        setFormData((prev) => {
            const filtered = prev.permissions.filter((entry) => validSet.has(entry));
            if (filtered.length === prev.permissions.length) return prev;
            return { ...prev, permissions: filtered };
        });
    }, [availablePermissions, role, mode, open]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleCategoryToggle = (categoryName, children, checked) => {
        setFormData(prev => {
            let newPermissions = [...prev.permissions];

            if (checked) {
                // Add category, remove any individual children
                newPermissions = newPermissions.filter(p => !children.includes(p));
                newPermissions.push(categoryName);
            } else {
                // Remove category
                newPermissions = newPermissions.filter(p => p !== categoryName);
            }

            return { ...prev, permissions: newPermissions };
        });
    };

    const handleChildToggle = (categoryName, childName, allChildren, checked) => {
        setFormData(prev => {
            let newPermissions = [...prev.permissions];

            // Remove category if it exists
            newPermissions = newPermissions.filter(p => p !== categoryName);

            if (checked) {
                // Add child
                if (!newPermissions.includes(childName)) {
                    newPermissions.push(childName);
                }
            } else {
                // Remove child
                newPermissions = newPermissions.filter(p => p !== childName);
            }

            return { ...prev, permissions: newPermissions };
        });
    };

    const isCategorySelected = (categoryName, children) => {
        return formData.permissions.includes(categoryName);
    };

    const isChildSelected = (categoryName, childName) => {
        // Child is selected if it's in permissions and category is NOT selected
        return formData.permissions.includes(childName) && !formData.permissions.includes(categoryName);
    };

    const filteredPermissions = availablePermissions.filter((p) => {
        if (!permissionSearch) return true;
        const term = permissionSearch.toLowerCase();
        if (p.name.toLowerCase().includes(term)) return true;
        if (p.description?.toLowerCase().includes(term)) return true;
        if (p.children?.some((c) => c.toLowerCase().includes(term))) return true;
        return false;
    });

    const handleSelectAll = () => {
        const allCategoryNames = availablePermissions.map((p) => p.name);
        setFormData((prev) => ({ ...prev, permissions: allCategoryNames }));
    };

    const handleDeselectAll = () => {
        setFormData((prev) => ({ ...prev, permissions: [] }));
    };

    const handleSave = () => {
        const dataToSave = {
            name: formData.name,
            type: formData.type,
            level: parseInt(formData.level),
            description: formData.description,
            permissions: formData.permissions,
        };
        onSave(dataToSave, () => handleClose());
    };

    const handleClose = () => {
        setFormData({
            name: '',
            type: 'General',
            level: 1,
            description: '',
            permissions: [],
        });
        setPermissionSearch('');
        onClose();
    };

    const isValid = () => {
        return formData.name.trim() !== '' &&
               formData.type !== '' &&
               formData.level >= 1 &&
               !isNaN(parseInt(formData.level));
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>{mode === 'create' ? 'Create' : 'Edit'} Role</DialogTitle>
            <DialogContent>
                <FirstFormField>
                    <TextField
                        fullWidth
                        label="Role Name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        helperText="e.g., Administrator, Project Manager"
                    />
                </FirstFormField>

                <Row>
                    <HalfWidthField>
                        <FormControl fullWidth required>
                            <InputLabel>Type</InputLabel>
                            <Select
                                name="type"
                                value={formData.type}
                                onChange={handleChange}
                                label="Type"
                            >
                                <MenuItem value="Internal">Internal</MenuItem>
                                <MenuItem value="General">General</MenuItem>
                            </Select>
                        </FormControl>
                    </HalfWidthField>
                    <HalfWidthField>
                        <TextField
                            fullWidth
                            type="number"
                            label="Level"
                            name="level"
                            value={formData.level}
                            onChange={handleChange}
                            helperText="Lower level = more authority"
                            required
                            inputProps={{ min: 1, step: 1 }}
                        />
                    </HalfWidthField>
                </Row>

                <FormField>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Description"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        helperText="Optional description of this role"
                    />
                </FormField>

                <FormField>
                    <PermissionsHeaderRow>
                        <Typography variant="subtitle1">
                            Assign Permissions
                        </Typography>
                        <HeaderActionsRow>
                            <Button size="small" onClick={handleSelectAll}>Select All</Button>
                            <Button size="small" onClick={handleDeselectAll}>Deselect All</Button>
                            <Chip
                                label={`${formData.permissions.length} selected`}
                                size="small"
                                color={formData.permissions.length > 0 ? 'primary' : 'default'}
                                variant="outlined"
                            />
                        </HeaderActionsRow>
                    </PermissionsHeaderRow>
                    <PermissionsHelperText variant="caption" color="text.secondary">
                        Select entire categories or individual permissions. Selecting a category grants all its children automatically.
                    </PermissionsHelperText>
                    <PermissionsSearchField
                        fullWidth
                        size="small"
                        placeholder="Search permissions..."
                        value={permissionSearch}
                        onChange={(e) => setPermissionSearch(e.target.value)}
                    />
                    <PermissionsList>
                        {availablePermissions.length === 0 ? (
                            <EmptyStateText variant="body2" color="text.secondary">
                                Loading permissions...
                            </EmptyStateText>
                        ) : filteredPermissions.length === 0 ? (
                            <EmptyStateText variant="body2" color="text.secondary">
                                No permissions match "{permissionSearch}"
                            </EmptyStateText>
                        ) : (
                            filteredPermissions.map(permission => {
                                const categorySelected = isCategorySelected(permission.name, permission.children || []);
                                const childrenCount = permission.children?.length || 0;
                                const selectedChildrenCount = (permission.children || []).filter(
                                    child => formData.permissions.includes(child)
                                ).length;
                                const hasSelection = categorySelected || selectedChildrenCount > 0;

                                return (
                                    <PermissionAccordion
                                        key={permission.name}
                                        disableGutters
                                        hasSelection={hasSelection}
                                    >
                                        <PermissionAccordionSummary
                                            expandIcon={childrenCount > 0 ? <ExpandMoreIcon /> : null}
                                        >
                                            <NoMarginCheckboxLabel
                                                control={
                                                    <Checkbox
                                                        checked={categorySelected}
                                                        onChange={(e) => {
                                                            e.stopPropagation();
                                                            handleCategoryToggle(permission.name, permission.children || [], e.target.checked);
                                                        }}
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                }
                                                label=""
                                            />
                                            <PermissionInfo>
                                                <Typography variant="body2" fontWeight="bold">
                                                    {permission.name}
                                                </Typography>
                                                {permission.description && (
                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                        {permission.description}
                                                    </Typography>
                                                )}
                                            </PermissionInfo>
                                            {childrenCount > 0 && (
                                                <CountChip
                                                    label={categorySelected ? `All ${childrenCount}` : `${selectedChildrenCount}/${childrenCount}`}
                                                    size="small"
                                                    variant="outlined"
                                                    color={hasSelection ? 'primary' : 'default'}
                                                />
                                            )}
                                        </PermissionAccordionSummary>
                                        {childrenCount > 0 && (
                                            <ChildrenDetails>
                                                {permission.children.map((child, idx) => (
                                                    <ChildCheckboxLabel
                                                        key={idx}
                                                        control={
                                                            <Checkbox
                                                                size="small"
                                                                checked={isChildSelected(permission.name, child) || categorySelected}
                                                                disabled={categorySelected}
                                                                onChange={(e) => handleChildToggle(permission.name, child, permission.children, e.target.checked)}
                                                            />
                                                        }
                                                        label={
                                                            <Typography variant="body2">
                                                                {child}
                                                            </Typography>
                                                        }
                                                    />
                                                ))}
                                            </ChildrenDetails>
                                        )}
                                    </PermissionAccordion>
                                );
                            })
                        )}
                    </PermissionsList>
                </FormField>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Cancel</Button>
                <Button variant="contained" color="primary" onClick={handleSave} disabled={!isValid()}>
                    {mode === 'create' ? 'Create' : 'Update'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default RoleDialog;
